import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Check } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';

const EXIT_NODES = [
  { cc: 'US', city: 'New York', type: 'MOB', net: 'Verizon 5G' },
  { cc: 'DE', city: 'Frankfurt', type: 'RES', net: 'Deutsche Telekom' },
  { cc: 'GB', city: 'London', type: 'MOB', net: 'EE 4G' },
  { cc: 'JP', city: 'Tokyo', type: 'RES', net: 'NTT' },
  { cc: 'BR', city: 'São Paulo', type: 'RES', net: 'Vivo' },
  { cc: 'FR', city: 'Paris', type: 'MOB', net: 'Orange 5G' },
  { cc: 'CA', city: 'Toronto', type: 'RES', net: 'Rogers' },
  { cc: 'IN', city: 'Mumbai', type: 'MOB', net: 'Jio 4G' },
  { cc: 'AU', city: 'Sydney', type: 'RES', net: 'Telstra' },
  { cc: 'NL', city: 'Amsterdam', type: 'RES', net: 'KPN' },
  { cc: 'MX', city: 'Mexico City', type: 'MOB', net: 'Telcel' },
  { cc: 'PL', city: 'Warsaw', type: 'RES', net: 'Orange PL' },
  { cc: 'ES', city: 'Madrid', type: 'MOB', net: 'Movistar' },
  { cc: 'SG', city: 'Singapore', type: 'RES', net: 'Singtel' },
  { cc: 'IT', city: 'Milan', type: 'RES', net: 'TIM' },
  { cc: 'KR', city: 'Seoul', type: 'MOB', net: 'SK Telecom' },
];

const TICKER = [
  'United States', 'Germany', 'United Kingdom', 'Japan', 'Brazil', 'France', 'Canada', 'India',
  'Australia', 'Netherlands', 'Mexico', 'Poland', 'Spain', 'Singapore', 'Italy', 'South Korea',
  'Turkey', 'Indonesia', 'Sweden', 'South Africa',
];

const rand = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

let seq = 0;
const makeHop = () => {
  const node = EXIT_NODES[rand(0, EXIT_NODES.length - 1)];
  seq += 1;
  return {
    id: seq,
    ...node,
    ip: `${rand(23, 213)}.${rand(1, 254)}.•••.${rand(2, 254)}`,
    ms: node.type === 'MOB' ? rand(180, 460) : rand(90, 320),
  };
};

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const RoutingConsole = () => {
  const [hops, setHops] = useState(() => Array.from({ length: 6 }, makeHop));
  const [bars, setBars] = useState(() => Array.from({ length: 32 }, () => rand(25, 95)));

  useEffect(() => {
    if (prefersReducedMotion()) return undefined;
    const id = setInterval(() => {
      setHops((prev) => [makeHop(), ...prev].slice(0, 6));
      setBars((prev) => [...prev.slice(1), rand(25, 95)]);
    }, 1600);
    return () => clearInterval(id);
  }, []);

  const current = hops[0];

  return (
    <div className="frame-ticks relative border border-slate-700 bg-slate-950/80 backdrop-blur-sm font-mono text-[12px] shadow-[0_40px_120px_-40px_rgba(255,91,31,0.45)]" aria-hidden="true">
      <div className="flex items-center justify-between border-b border-slate-800 px-4 py-2.5 text-slate-400">
        <span>gw.ip9.io<span className="text-slate-600">:7777</span></span>
        <span className="flex items-center gap-2 text-cyan-400">
          <span className="h-1.5 w-1.5 bg-cyan-400 animate-pulse" />
          ROTATING
        </span>
      </div>

      <div className="border-b border-slate-800 px-4 py-3 text-slate-300 leading-relaxed">
        <span className="text-cyan-400">$</span> curl -x gw.ip9.io:7777 \<br />
        <span className="pl-4 inline-block">-U "user-country-<span className="text-cyan-300">{current.cc.toLowerCase()}</span>:••••" \</span><br />
        <span className="pl-4 inline-block">https://api.ipify.org</span>
        <div key={current.id} className="mt-2 text-green-400 animate-row-in">
          → 200 OK · {current.ip} · {current.city}, {current.cc} · {current.ms}ms
        </div>
      </div>

      <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-[0.14em] text-slate-500 grid grid-cols-[2.25rem_1fr_2.5rem_4rem] gap-2">
        <span>Geo</span><span>Exit node</span><span>Type</span><span className="text-right">Latency</span>
      </div>
      <ul className="px-4 pb-3">
        {hops.map((hop, i) => (
          <li
            key={hop.id}
            className={`grid grid-cols-[2.25rem_1fr_2.5rem_4rem] gap-2 items-center py-1.5 border-b border-slate-900 last:border-0 ${i === 0 ? 'animate-row-in text-slate-100' : 'text-slate-400'}`}
            style={{ opacity: 1 - i * 0.12 }}
          >
            <span className={i === 0 ? 'text-cyan-400' : ''}>{hop.cc}</span>
            <span className="truncate">{hop.city} <span className="text-slate-600">/ {hop.net}</span></span>
            <span className={hop.type === 'MOB' ? 'text-purple-400' : 'text-slate-300'}>{hop.type}</span>
            <span className="flex items-center justify-end gap-2">
              <span className="hidden sm:block h-1 bg-slate-800 w-8 overflow-hidden">
                <span className="block h-full bg-cyan-500" style={{ width: `${Math.min(100, hop.ms / 5)}%` }} />
              </span>
              {hop.ms}ms
            </span>
          </li>
        ))}
      </ul>

      <div className="border-t border-slate-800 px-4 py-3">
        <div className="flex justify-between text-[10px] uppercase tracking-[0.14em] text-slate-500 mb-2">
          <span>Requests / s</span>
          <span className="text-slate-300">{(bars[bars.length - 1] * 137).toLocaleString('en-US')}</span>
        </div>
        <div className="flex items-end gap-[3px] h-10">
          {bars.map((h, i) => (
            <span key={i} className={`flex-1 ${i === bars.length - 1 ? 'bg-cyan-400' : 'bg-slate-700'}`} style={{ height: `${h}%` }} />
          ))}
        </div>
      </div>
    </div>
  );
};

const HeroSection = ({ title1, title2, subtitle, className = "", priceBadge }) => {
  const { t } = useTranslation();

  const displayTitle1 = title1 || t('hero.title1');
  const displayTitle2 = title2 || t('hero.title2');
  const displaySubtitle = subtitle || t('hero.subtitle');

  const specs = useMemo(() => [
    { value: '300M+', label: t('hero.ipsWorldwide') },
    { value: '200+', label: t('hero.countries') },
    { value: '99.9%', label: t('hero.uptime') },
  ], [t]);

  return (
    <section className="grain relative overflow-hidden bg-slate-950 border-b border-slate-800">
      <div className="absolute inset-0 bg-grid bg-grid-fade" />
      <div className="pointer-events-none absolute left-1/2 top-[62%] h-[70rem] w-[120rem] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(255,91,31,0.28),rgba(255,91,31,0.06)_55%,transparent_75%)]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-500/60 to-transparent" />

      <div className={`relative max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24 ${className || 'pb-20 md:pb-28'}`}>
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-3 border border-slate-700 bg-slate-900/60 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-slate-300 mb-8">
              {priceBadge ? (
                <>
                  <Check className="w-3.5 h-3.5 text-cyan-400" />
                  <span>{priceBadge}</span>
                </>
              ) : (
                <>
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping bg-green-400 opacity-60" />
                    <span className="relative inline-flex h-2 w-2 bg-green-400" />
                  </span>
                  <span>Network live · {t('hero.countries')}</span>
                </>
              )}
            </div>

            <h1 className="font-display font-bold text-[2.75rem] leading-[0.95] sm:text-6xl lg:text-[3.75rem] xl:text-[4.25rem] text-slate-50 mb-7">
              <span className="block">{displayTitle1}</span>
              <span className="relative inline-block text-cyan-500">
                {displayTitle2}
                <span className="absolute -bottom-2 left-0 h-[3px] w-1/3 bg-cyan-500" />
              </span>
            </h1>

            <p className="text-lg md:text-xl text-slate-400 mb-10 max-w-2xl leading-relaxed">
              {displaySubtitle}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-12">
              <a
                href="https://dashboard.ip9.io"
                className="btn-glow group inline-flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-slate-950 px-7 h-12 text-base font-semibold transition-colors"
              >
                {t('hero.startTrial')}
                <ArrowUpRight className="w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-2 border border-slate-600 hover:border-slate-300 text-slate-200 hover:text-white px-7 h-12 text-base font-medium transition-colors"
              >
                {t('hero.viewPricing')}
              </Link>
            </div>

            <dl className="grid grid-cols-3 border-y border-slate-800 divide-x divide-slate-800">
              {specs.map((s) => (
                <div key={s.value} className="py-4 pr-3 pl-3 first:pl-0">
                  <dt className="sr-only">{s.label}</dt>
                  <dd className="font-display text-2xl md:text-3xl font-bold text-slate-50">{s.value}</dd>
                  <dd className="font-mono text-[10px] md:text-[11px] uppercase tracking-[0.12em] text-slate-500 mt-1 leading-snug">{s.label}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="lg:col-span-5">
            <RoutingConsole />
          </div>
        </div>
      </div>

      <div className="relative border-t border-slate-800 bg-slate-950/80 overflow-hidden">
        <div className="flex w-max animate-marquee py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">
          {[...TICKER, ...TICKER].map((c, i) => (
            <span key={i} className="flex items-center px-6">
              <span className="mr-6 h-1 w-1 bg-cyan-500" />
              {c}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
